import { defineStore } from 'pinia';
import Network from "~/utils/network";
import { useCountryStore } from './country';
import { i18nInstance } from '~~/plugins/i18n';
import {ofetch} from "ofetch";

const urls = {
  getFeaturedPartners: (countryId) => `/api/administration/featured-partners/${countryId}`,
  getTopPartners: (countryId) => `/api/administration/top-partners/${countryId}`,
  getRunningCampaign: (countryId) => `/api/customer/mgm/campaign/running/${countryId}`,
  getMyReferralOverview: (campaignId) => `/customer/mgm/referrer/overview/campaign/${campaignId}`,
  getMyAllReferralOverview: (countryId) => `/customer/mgm/referrer/overview/country/${countryId}`,
  getMyReferrals: (campaignId) => `/customer/mgm/referrer/my/referrals-list/${campaignId}`,
  getAllMyReferrals: (countryId) => `/customer/mgm/referrer/all/my-referrals-list/${countryId}`,
  signUp: (referralCode, phone) => `/api/customer/external/mgm/referral/signup/${referralCode}/${phone}`,
  sendFollowUp: (referralId) => `/customer/mgm/referrer/follow-up/${referralId}`,
  fetchRewardEligibleMerchants: `/merchant/reward_eligible`,
  myCouponOverview: (countryId) => `/customer/mgm/coupon/my/${countryId}`,
  myCoupons: `/customer/mgm/coupon/my/coupons`,
  useCoupon: (merchantId, countryId) => `/customer/mgm/coupon/used/${merchantId}/${countryId}`,
  createReferralCode: (campaignId) => `/customer/mgm/referrer/create/${campaignId}`,
  myAllRewards: `/customer/mgm/referrer/my-all-rewards`,
  getEarningHistory: `/customer/mgm/referrer/earning-history`,
  getHasBeenIncluded: (financier) => `/customer/mgm/campaign/has-been-included/${financier}`,
  getIsIncluded: (financier) => `/customer/mgm/campaign/is-included/${financier}`,
};

export const useReferralStore = defineStore("referral", {
  state: () => ({
    featuredPartners: null,
    topPartners: null,
    runningCampaign: null,
    logos: {},
    myAllRewards: null,
    myReferrals: null,
    allMyReferrals: null,
    myReferralOverview: null,
    myAllReferralOverview: null,
    bnplBaseUrl: useRuntimeConfig().public.bnplBaseUrl,
    landingPageData: {},
    signedUp: false,
    rewardEligibleMerchants: null,
    myCouponOverview: {
      totalAmount: null,
      usedAmount: null,
    },
    myCoupons: null,
    earningHistory: null,
    hasBeenIncluded: false,
    isIncluded: false,
  }),
  actions: {
    _cleanupUnusedLogos() {
      const usedKeys = new Set();
      [this.featuredPartners, this.topPartners, this.rewardEligibleMerchants, this.myCoupons].forEach(list => {
        if (list) list.forEach(item => {
          const key = item.logo || item.merchantLogo;
          if (key) usedKeys.add(key);
        });
      });
      Object.keys(this.logos).forEach(key => {
        if (!usedKeys.has(key)) {
          URL.revokeObjectURL(this.logos[key]);
          delete this.logos[key];
        }
      });
    },
    async fetchFeaturedPartners() {
      try {
        this.featuredPartners = await ofetch(urls.getFeaturedPartners(this.landingPageData.countryId));
        this.featuredPartners.forEach(async partner => {
          if (partner.logo && !this.logos[partner.logo]) {
            try {
              this.logos[partner.logo] = URL.createObjectURL(await ofetch(`/api/file/public/${partner.logo}`, {responseType: 'blob'}));
            } catch {}
          }
        });
        this._cleanupUnusedLogos();
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    async fetchTopPartners() {
      try {
        this.topPartners = await ofetch(urls.getTopPartners(this.landingPageData.countryId));
        this.topPartners.forEach(async partner => {
          if (partner.logo && !this.logos[partner.logo]) {
            try {
              this.logos[partner.logo] = URL.createObjectURL(await ofetch(`/api/file/public/${partner.logo}`, {responseType: 'blob'}));
            } catch {}
          }
        });
        this._cleanupUnusedLogos();
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    async getRunningCampaign() {
      try {
        const countryStore = useCountryStore();
        this.runningCampaign = await ofetch(urls.getRunningCampaign(countryStore.countryId));
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    async getMyReferralOverview(campaignId) {
      try {
        this.myReferralOverview = await Network.http(urls.getMyReferralOverview(campaignId));
        if (!this.referralCode) {
          await this.createReferralCode(campaignId);
          this.myReferralOverview = await Network.http(urls.getMyReferralOverview(campaignId));
        }
      } catch (err) {
        if (err.status === 404) {
          try {
            await this.createReferralCode(campaignId);
            this.myReferralOverview = await Network.http(urls.getMyReferralOverview(campaignId));
          } catch (error) {
            console.log(error);
            await Promise.reject(error);
          }
        }
        console.log(err);
        await Promise.reject(err);
      }
    },
    async getMyAllReferralOverview(countryId) {
      try {
        this.myAllReferralOverview = await Network.http(urls.getMyAllReferralOverview(countryId));
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    async createReferralCode(campaignId) {
      try {
        await Network.http(urls.createReferralCode(campaignId), { method: "POST" });
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    async getMyReferrals(campaignId) {
      try {
        this.myReferrals = await Network.http(urls.getMyReferrals(campaignId));
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    async getAllMyReferrals(countryId) {
      try {
        this.allMyReferrals = await Network.http(urls.getAllMyReferrals(countryId));
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    loadLandingPageData(data) {
      this.landingPageData = data;
    },
    async signUp(referralCode, phone) {
      try {
        useFetch(urls.signUp(referralCode, phone), { method: "POST" })
        .then((response) => {
          if (response.error && response.error.value && response.error.value.status !== 200) {
            useToast().add({
              message: i18nInstance.global.t("bnpl:referral.landing_page.already_signed_up_error"),
              duration: 3000,
              type: "error",
            });
          } else {
            this.signedUp = true;
          }
        })
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    async sendFollowUp(referralId) {
      try {
        await Network.http(urls.sendFollowUp(referralId), { method: "POST" });
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    async fetchRewardEligibleMerchants() {
      try {
        this.rewardEligibleMerchants = await Network.http(urls.fetchRewardEligibleMerchants);
        await this.rewardEligibleMerchants.forEach(async merchant => {
          if (merchant.logo && !this.logos[merchant.logo]) {
            try {
              this.logos[merchant.logo] = URL.createObjectURL(await ofetch(`/api/file/public/${merchant.logo}`, {responseType: 'blob'}));
            } catch {}
          }
        });
        this._cleanupUnusedLogos();
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    async fetchMyCouponOverview() {
      try {
        const countryStore = useCountryStore();
        this.myCouponOverview = await Network.http(urls.myCouponOverview(countryStore.countryId));
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    async fetchMyCoupons() {
      try {
        this.myCoupons = await Network.http(urls.myCoupons);
        await this.myCoupons.forEach(async coupon => {
          if (coupon.merchantLogo && !this.logos[coupon.merchantLogo]) {
            try {
              this.logos[coupon.merchantLogo] = URL.createObjectURL(await ofetch(`/api/file/public/${coupon.merchantLogo}`, {responseType: 'blob'}));
            } catch {}
          }
        });
        this._cleanupUnusedLogos();
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    async useCoupon(merchantId) {
      try {
        const countryStore = useCountryStore();
        await Network.http(urls.useCoupon(merchantId, countryStore.countryId), { method: "POST" });
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    async fetchMyAllRewards() {
      try {
        this.myAllRewards = await Network.http(urls.myAllRewards);
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    async fetchEarningHistory() {
      try {
        this.earningHistory = await Network.http(urls.getEarningHistory);
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    async fetchHasBeenIncluded(financier) {
      try {
        this.hasBeenIncluded = await Network.http(urls.getHasBeenIncluded(financier));
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
    async fetchIsIncluded(financier) {
      try {
        this.isIncluded = await Network.http(urls.getIsIncluded(financier));
      } catch (err) {
        console.log(err);
        await Promise.reject(err);
      }
    },
  },
  getters: {
    referralCode() {
      if (this.myReferralOverview && this.myReferralOverview.referralCode) {
        return this.myReferralOverview.referralCode;
      }
      return null;
    },
    referralUrl() {
      return `${this.bnplBaseUrl}/referral?code=${this.referralCode}`;
    },
    getMyReferralById() {
      return (id) => this.myReferrals.find(value => value.id === id);
    },
    overview() {
      if (this.myReferralOverview) {
        return {
          earnedReward: this.myReferralOverview.earnedReward ?? 0,
          completed: this.myReferralOverview.actionCompleted ?? 0,
          pending: (this.myReferralOverview.referees - this.myReferralOverview.actionCompleted) ?? 0,
        };
      }
      return {
        earnedReward: 0,
        completed: 0,
        pending: 0,
      };
    },
    allOverview() {
      if (this.myAllReferralOverview) {
        return {
          earnedReward: this.myAllReferralOverview.earnedReward ?? 0,
          completed: this.myAllReferralOverview.actionCompleted ?? 0,
          pending: (this.myAllReferralOverview.referees - this.myAllReferralOverview.actionCompleted) ?? 0,
        };
      }
      return {
        earnedReward: 0,
        completed: 0,
        pending: 0,
      };
    },
    getLandingPageCountry() {
      return {
        id: this.landingPageData.countryId,
        languageIso: this.landingPageData.languageIso,
        phoneFormat: this.landingPageData.phoneFormat,
        phoneCountryCode: this.landingPageData.phoneCountryCode,
        currencyIso: this.landingPageData.currencyIso,
        lastNameFirst: this.landingPageData.lastNameFirst,
      }
    }
  },
});